/**
 * Cloudflare Turnstile plumbing for the contact form.
 *
 * Kept out of the component so the key resolution and the script loading can be
 * exercised without mounting a widget that needs a real Cloudflare origin.
 */

export const TURNSTILE_SCRIPT_URL = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit"

export type TurnstileRenderOptions = {
  sitekey: string
  callback: (token: string) => void
  "error-callback": () => void
  "expired-callback": () => void
  language?: string
  theme?: "light" | "dark" | "auto"
}

export type TurnstileApi = {
  render: (container: HTMLElement, options: TurnstileRenderOptions) => string
  remove: (widgetId: string) => void
  reset: (widgetId?: string) => void
}

declare global {
  interface Window {
    turnstile?: TurnstileApi
  }
}

/**
 * Absent until Cloudflare keys are provisioned. The form then submits without a
 * token, which the mailer accepts because it skips verification whenever its own
 * secret is unset — the two sides are gated on the same piece of configuration.
 */
export function resolveTurnstileSiteKey(
  configuredKey = import.meta.env.PUBLIC_TURNSTILE_SITE_KEY
): string | null {
  const key = configuredKey?.trim()
  return key ? key : null
}

let scriptPromise: Promise<void> | null = null

export function loadTurnstileScript(doc: Document = document): Promise<void> {
  if (scriptPromise) return scriptPromise

  scriptPromise = new Promise<void>((resolve, reject) => {
    const existing = doc.querySelector<HTMLScriptElement>(`script[src="${TURNSTILE_SCRIPT_URL}"]`)
    if (existing) {
      existing.addEventListener("load", () => resolve())
      existing.addEventListener("error", () => reject(new Error("Turnstile script failed to load")))
      return
    }

    const script = doc.createElement("script")
    script.src = TURNSTILE_SCRIPT_URL
    script.async = true
    script.defer = true
    script.addEventListener("load", () => resolve())
    script.addEventListener("error", () => reject(new Error("Turnstile script failed to load")))
    doc.head.appendChild(script)
  })

  return scriptPromise
}

/** Test seam: the module-level promise would otherwise leak between cases. */
export function resetTurnstileScriptState() {
  scriptPromise = null
}
